/**
 * Comprehensive filename sanitization function
 * Handles invalid characters, reserved names, whitespace, and length limits
 * @param {string} filename - The filename to sanitize
 * @param {string} defaultName - Default name if filename is invalid (default: 'file')
 * @param {string} defaultExtension - Default extension if none provided (default: 'txt')
 * @param {number} maxLength - Maximum filename length (default: 200)
 * @returns {string} - Sanitized filename
 */
const sanitizeFilename = (filename: string, defaultName = 'file', defaultExtension = 'xlsx', maxLength = 200) => {
  // Handle null, undefined, or empty strings
  if (!filename || typeof filename !== 'string') {
    return `${defaultName}.${defaultExtension}`
  }

  // Windows reserved names (case-insensitive)
  const reservedNames = [
    'CON',
    'PRN',
    'AUX',
    'NUL',
    'COM1',
    'COM2',
    'COM3',
    'COM4',
    'COM5',
    'COM6',
    'COM7',
    'COM8',
    'COM9',
    'LPT1',
    'LPT2',
    'LPT3',
    'LPT4',
    'LPT5',
    'LPT6',
    'LPT7',
    'LPT8',
    'LPT9'
  ]

  // Step 1: Extract extension FIRST before character replacement
  const lastDotIndex = filename.lastIndexOf('.')
  let name = filename
  let extension = defaultExtension

  if (lastDotIndex > 0 && lastDotIndex < filename.length - 1) {
    // Found a valid extension (dot not at start or end)
    const possibleExtension = filename.substring(lastDotIndex + 1)
    // Only treat as extension if it's reasonable (no spaces, reasonable length)
    if (possibleExtension.length <= 10 && !/\s/.test(possibleExtension)) {
      extension = possibleExtension
      name = filename.substring(0, lastDotIndex)
    }
  }

  // Step 2: Remove or replace invalid characters in NAME only
  let sanitized = name
    // Remove invalid Windows/Unix characters
    .replace(/[<>:"/\\|?*]/g, '-')
    // Remove control characters (0x00-0x1f, 0x7f-0x9f)
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f-\x9f]/g, '')
    // Replace Unicode emojis and other problematic characters
    .replace(
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu,
      '-'
    )
    // Replace other problematic characters
    .replace(/[#%&{}+~`@$]/g, '-')
    // Replace remaining dots with dashes (since extension is already handled)
    .replace(/\./g, '-')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Trim whitespace from start and end
    .trim()

  // Step 3: Handle path-like structures
  sanitized = sanitized
    // Remove parent directory references
    .replace(/\.\.\//g, '')
    .replace(/\.\./g, '')
    // Remove current directory references
    .replace(/\.\//g, '')
    // Remove leading dots
    .replace(/^\.+/, '')

  // Update name variable
  name = sanitized

  // Clean up name part
  name = name
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')

  // Clean up extension
  extension = extension
    ?.replace(/\./g, '')
    // Replace spaces and special chars in extension
    .replace(/[^a-zA-Z0-9]/g, '')
    // Limit extension length
    .substring(0, 10)

  // Step 4: Handle empty or invalid names
  if (!name || name.length === 0) {
    name = defaultName
  }

  if (!extension || extension.length === 0) {
    extension = defaultExtension
  }

  // Step 5: Check for reserved names
  const nameWithoutExtension = name.toUpperCase()
  const fullNameUpper = `${name}.${extension}`.toUpperCase()

  if (reservedNames.includes(nameWithoutExtension) || reservedNames.includes(fullNameUpper)) {
    name = `${defaultName}-${name}`
  }

  // Step 6: Construct final filename
  let finalFilename = `${name}.${extension}`

  // Step 7: Handle length limits
  if (finalFilename.length > maxLength) {
    const extensionLength = extension.length + 1 // +1 for the dot
    const maxNameLength = maxLength - extensionLength

    if (maxNameLength > 0) {
      name = name.substring(0, maxNameLength)
      finalFilename = `${name}.${extension}`
    } else {
      // If extension is too long, truncate it too
      extension = extension.substring(0, 10)
      name = name.substring(0, maxLength - extension.length - 1)
      finalFilename = `${name}.${extension}`
    }
  }

  // Step 8: Final validation - ensure it's not empty
  if (!finalFilename || finalFilename === '.' || finalFilename === `${defaultExtension}`) {
    finalFilename = `${defaultName}.${defaultExtension}`
  }

  return finalFilename
}

// Export the function
export default sanitizeFilename
